import { type DetailField, DetailSections, InlineMessage, Stack } from '@olytic/foundation';

import { formatDateTime } from './format';
import { type MergeHistory, movedSummary, truncationNote, UNKNOWN_MERGER } from './mergeHistory';

/**
 * The record page's "Merged Records" section (MOTHER-180): the records merged into this
 * account or contact, newest first as GET /api/merge/history sends them, so nobody has to
 * guess later why it carries another's contacts. Each merge is four read-only fields: the
 * merged record's name (plain text: it is in the Recycle Bin or gone), who merged it, when
 * (as History shows times) and what moved. Shown only for a history with merges in it
 * (useMergeHistory answers null otherwise).
 */
export function MergedRecordsSection({ history, singular }: { history: MergeHistory; singular: string }): JSX.Element {
  const note = truncationNote(history);
  const fields = history.merges.flatMap<DetailField>((merge, index) => {
    const key = `merge:${index}:${merge.merged_id}`;
    return [
      { key: `${key}:name`, label: 'Merged Record', value: merge.merged_name || `Unnamed ${singular.toLowerCase()}`, wide: true },
      { key: `${key}:by`, label: 'Merged By', value: merge.merged_by_name || UNKNOWN_MERGER },
      { key: `${key}:at`, label: 'Merged At', value: formatDateTime(merge.merged_at) },
      { key: `${key}:moved`, label: 'Moved', value: movedSummary(merge.moved), wide: true },
    ];
  });
  return (
    // DetailSections labels its own <section> "Merged Records"; the note on a cut list follows it.
    <Stack gap="sm">
      <DetailSections sections={[{ key: 'merged-records', label: 'Merged Records', fields }]} />
      {note ? <InlineMessage tone="info">{note}</InlineMessage> : null}
    </Stack>
  );
}
